import { Routes } from '@angular/router';
import { AuthGuard } from './route-guards/redirect.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/home/home.component').then((m) => m.HomeComponent),
    data: {
      seo: {
        title: 'WhatsApp, CRM & Instagram AI Automation for Businesses',
        description: 'Automate customer support, lead management, and instant WhatsApp messaging using Nexchat AI Agent.',
        keywords: 'Nexchat AI, WhatsApp chatbot, AI assistant, lead automation, business chatbot, AI customer support',
        robots: 'index, follow',
        jsonLdSchema: {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Nexchat AI Agent',
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'All',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        }
      }
    }
  },
  {
    path: 'website',
    loadComponent: () =>
      import('./pages/nexchat-website/nexchat-website.component').then(
        (m) => m.NexChatWebsiteComponent
      ),
    data: {
      seo: {
        title: 'NexChat — Automate Instagram. Convert Followers into Sales.',
        description: 'Creator-first Instagram automation, lead capture, and AI sales platform for creators, D2C brands, and agencies.',
        keywords: 'Instagram automation, comment to DM, AI sales agent, Instagram leads, NexChat creator platform',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'signup',
    loadComponent: () =>
      import('./pages/signUp/sign-up.component').then((m) => m.SignUpComponent),
    data: {
      seo: {
        title: 'Get Started Free | Sign Up',
        description: 'Create your Nexchat AI Agent account today and start automating customer conversations on WhatsApp.',
        keywords: 'Nexchat signup, create AI chatbot account, start chatbot automation',
        robots: 'noindex, follow'
      }
    }
  },
  {
    path: 'signin',
    loadComponent: () =>
      import('./pages/signIn/sign-in.component').then((m) => m.SignInComponent),
    data: {
      seo: {
        title: 'Sign In | Dashboard Access',
        description: 'Sign in to your Nexchat AI Agent dashboard to manage leads, campaigns, and WhatsApp flows.',
        keywords: 'Nexchat login, AI chatbot signin, chatbot dashboard',
        robots: 'noindex, follow'
      }
    }
  },
  {
    path: 'blog',
    loadComponent: () =>
      import('./component/blog/blog.component').then((m) => m.BlogComponent),
    data: {
      seo: {
        title: 'AI Automation & Marketing Blog',
        description: 'Discover practical guides, marketing tips, and AI chatbot best practices to scale your business operations.',
        keywords: 'AI chatbot blog, WhatsApp marketing tips, conversational AI guides, lead generation strategies',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./component/contact/contact.component').then(
        (m) => m.ContactComponent
      ),
    data: {
      seo: {
        title: 'Contact Us | Nexchat AI Support',
        description: 'Have questions or need assistance? Reach out to the Nexchat AI support team for expert guidance.',
        keywords: 'Nexchat support, contact AI team, chatbot assistance, customer service',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'faq',
    loadComponent: () =>
      import('./component/faq/faq.component').then((m) => m.FAQComponent),
    data: {
      seo: {
        title: 'Frequently Asked Questions (FAQ)',
        description: 'Find answers to common questions about setting up, integrating, and scaling with Nexchat AI Agent.',
        keywords: 'Nexchat FAQ, AI chatbot questions, WhatsApp API integration FAQ, chatbot pricing help',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'terms-of-service',
    loadComponent: () =>
      import('./shared/terms-of-service/terms-of-service.component').then(
        (m) => m.TermsOfServiceComponent
      ),
    data: {
      seo: {
        title: 'Terms of Service',
        description: 'Read the terms of service for using Nexchat AI Agent software and platform services.',
        keywords: 'terms of service, Nexchat agreement, terms and conditions',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./shared/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
    data: {
      seo: {
        title: 'Privacy Policy',
        description: 'Learn how Nexchat AI Agent protects, stores, and handles your business and customer data.',
        keywords: 'privacy policy, data protection, privacy guidelines',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'data-rights',
    loadComponent: () =>
      import('./shared/data-rights/data-rights.component').then(
        (m) => m.DataRightsComponent
      ),
    data: {
      seo: {
        title: 'Exercise Your Data Rights',
        description: 'Submit requests for data access, correction, erasure, or consent withdrawal under the DPDP Act.',
        keywords: 'data principal rights, delete my data, correct my data, DPDP rights',
        robots: 'noindex, follow'
      }
    }
  },
  {
    path: 'refund-policy',
    loadComponent: () =>
      import('./shared/refund-policy/refund-policy.component').then(
        (m) => m.RefundPolicyComponent
      ),
    data: {
      seo: {
        title: 'Refund Policy',
        description: 'Understand the refund policy and billing guidelines for Nexchat AI subscriptions.',
        keywords: 'refund policy, billing guidelines, subscription refund',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'ai-agent-use-cases',
    loadComponent: () =>
      import('./component/ai-agents-case/ai-agents-case.component').then(
        (m) => m.AiAgentsCaseComponent
      ),
    data: {
      seo: {
        title: 'AI Agent Use Cases & Industry Solutions',
        description: 'Explore real-world use cases of Nexchat AI in Real Estate, E-Commerce, Healthcare, and Lead Generation.',
        keywords: 'AI use cases, WhatsApp real estate bot, ecommerce AI assistant, healthcare chatbot',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'organizations/create',
    loadComponent: () =>
      import(
        './admin/organizations/create-organization/create-organization.component'
      ).then((m) => m.CreateOrganizationComponent),
    data: {
      seo: {
        title: 'Create Organization',
        robots: 'noindex, nofollow'
      }
    }
  },
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./pages/onboarding/onboarding-wizard.component').then(
        (m) => m.OnboardingWizardComponent
      ),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Account Onboarding',
        robots: 'noindex, nofollow'
      }
    }
  },
  // ✅ USER AREA
  {
    path: 'user',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./user/user.routes').then(m => m.USER_ROUTES),
    data: {
      seo: {
        title: 'User Portal | Dashboard',
        robots: 'noindex, nofollow'
      }
    }
  },
    {
    path: 'product-catalogs',
    loadComponent: () => import('../app/user/product-catalogs/product-catalogs.component').then(m => m.ProductCatalogsComponent),
    data: {
      seo: {
        title: 'Product Catalogs',
        description: 'Browse our product catalogs and explore available products.',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'catalogue',
    loadComponent: () => import('../app/user/product-catalogs/product-catalogs.component').then(m => m.ProductCatalogsComponent),
    data: {
      seo: {
        title: 'Digital Catalogue',
        description: 'Browse our digital product catalogue.',
        robots: 'index, follow'
      }
    }
  },

  // ✅ ADMIN AREA
  {
    path: 'admin',
    loadComponent: () =>
      import('./user/user.component').then((m) => m.UserComponent),
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./admin/admin.routes').then((m) => m.ADMIN_ROUTES),
    data: {
      seo: {
        title: 'Admin Portal | Dashboard',
        robots: 'noindex, nofollow'
      }
    }
  },

  {
    path: '**',
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
    data: {
      seo: {
        title: '404 - Page Not Found',
        description: 'The requested page could not be found.',
        robots: 'noindex, nofollow'
      }
    }
  },
];
